import { DownloadIcon, OrdersIcon } from 'core/components/Icons'
import { Badge, BadgeTone } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import axiosInstance from 'core/network/axiosInstance'
import { formatDateTime } from 'core/utils/format'
import React, { useState } from 'react'
import { OrderListItem, OrderStatus, formatOrderAddress } from '../../domain/entities/Order'

interface OrdersTableProps {
  orders: OrderListItem[]
  loading: boolean
  onReview: (order: OrderListItem) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
  emptyMessage?: string
}

const statusTones: Record<OrderStatus, BadgeTone> = {
  indent_raised: 'warning',
  order_placed: 'info',
  payment_received: 'info',
  order_confirmed: 'success',
  in_progress: 'secondary',
  dispatched: 'secondary',
  delivered: 'success',
}

const paymentStatusTones: Record<string, BadgeTone> = {
  pending: 'warning',
  uploaded: 'info',
  verified: 'success',
  rejected: 'danger',
}

export const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  loading,
  onReview,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  emptyMessage,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const handleDownloadPO = async (orderId: string) => {
    try {
      setDownloadingId(orderId)
      const response = await axiosInstance.get(`/api/v1/purchase-orders/by-order/${orderId}/download`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `PO-${orderId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      alert('Failed to download PO. It may not be generated yet.')
    } finally {
      setDownloadingId(null)
    }
  }

  const columns: Column<OrderListItem>[] = [
    {
      key: 'id',
      label: 'Order ID',
      className: 'font-semibold text-text-primary',
    },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (row) => (
        <span className="text-xs text-text-primary font-medium whitespace-nowrap">
          {formatDateTime(row.orderPlacedDate || row.createdAt)}
        </span>
      ),
    },
    {
      key: 'buyer',
      label: 'Buyer Info',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-text-primary leading-tight">
            {row.buyer?.businessName || row.buyer?.name || '—'}
          </span>
          <div className="flex items-center gap-1.5">
            {row.buyer?.role && (
              <Badge tone="neutral" variant="outline" className="capitalize text-[10px] px-1 py-0 font-bold shrink-0">
                {row.buyer.role}
              </Badge>
            )}
            <span className="text-[10px] text-text-secondary font-medium">{row.buyer?.phone}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'shipToAddress',
      label: 'Order Address',
      className: 'text-text-secondary text-xs max-w-[220px] truncate',
      render: (row) => {
        const address = formatOrderAddress(row.shipToAddress)
        return <span title={address}>{address}</span>
      },
    },
    {
      key: 'payment',
      label: 'Payment Status',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Badge tone={paymentStatusTones[row.paymentStatus] || 'neutral'} variant="subtle" className="capitalize">
            {row.paymentStatus || 'Awaiting Payment'}
          </Badge>
          <span className="text-xs text-text-secondary font-semibold uppercase">{row.paymentMode}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Order Status',
      render: (row) => (
        <Badge
          tone={row.status === 'delivered' && row.hasGrievance ? 'danger' : statusTones[row.status]}
          variant="subtle"
          className="capitalize"
        >
          {row.status.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => {
        const canDownloadPo = [
          'payment_received',
          'order_confirmed',
          'in_progress',
          'dispatched',
          'delivered',
        ].includes(row.status)

        return (
          <div className="flex items-center justify-center gap-2">
            {canDownloadPo && (
              <button
                onClick={() => handleDownloadPO(row.id)}
                disabled={downloadingId === row.id}
                className="p-1.5 rounded-btn hover:bg-bg text-primary hover:text-primary-hover transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)] disabled:opacity-50"
                title="Download Purchase Order"
              >
                {downloadingId === row.id ? (
                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <DownloadIcon size={14} />
                )}
                PO
              </button>
            )}
            <button
              onClick={() => onReview(row)}
              className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Review Details
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <Table
      columns={columns}
      data={orders}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage={emptyMessage || 'No cattle feed orders match your query parameters.'}
      emptyIcon={<OrdersIcon size={20} className="opacity-60" />}
    />
  )
}
